import { useEffect, useRef, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";

import {
  BrowserMultiFormatReader,
  BarcodeFormat,
  DecodeHintType,
  RGBLuminanceSource,
  BinaryBitmap,
  HybridBinarizer,
} from "@zxing/library";

import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

const formatarCodigoVisual = (valor) => {
  const s = String(valor || "").replace(/\s+/g, "");
  if (!s) return "";

  const grupos = [];
  let i = s.length;

  while (i > 0) {
    const ini = Math.max(0, i - 6);
    grupos.unshift(s.slice(ini, i));
    i = ini;
  }

  return grupos.join(" ");
};

const lerComZxing = async (video) => {
  const canvas = document.createElement("canvas");
  const escala = 2;

  canvas.width = video.videoWidth * escala;
  canvas.height = video.videoHeight * escala;

  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  const dataUrl = canvas.toDataURL("image/png");

  const img = new Image();
  img.src = dataUrl;

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });

  const reader = new BrowserMultiFormatReader();
  const resultado = await reader.decodeFromImageElement(img);

  return {
    codigo: resultado?.getText?.() || "",
    tipo: resultado?.getBarcodeFormat ? String(resultado.getBarcodeFormat()) : "zxing",
  };
};

export default function DialogBarcodeReader({ open, onClose }) {
  const { showAlert } = useAlert();

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const codigoInputRef = useRef(null);
  const html5QrRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [codigo, setCodigo] = useState("");
  const [tipoCodigo, setTipoCodigo] = useState("");
  const [resultado, setResultado] = useState(null);
  const [suportaDetector, setSuportaDetector] = useState(false);

  const [materiais, setMateriais] = useState([]);
  const [materialSelecionado, setMaterialSelecionado] = useState(null);
  const [materialInput, setMaterialInput] = useState("");
  const [quantidadeAssoc, setQuantidadeAssoc] = useState("1");
  const [associarLoading, setAssociarLoading] = useState(false);

  const [modoOperacao, setModoOperacao] = useState("");
  const [quantidadeMov, setQuantidadeMov] = useState("1");
  const [documento, setDocumento] = useState("");
  const [localizacao, setLocalizacao] = useState("");

  const carregarMateriais = async (texto) => {
    const q = (texto || "").trim();

    if (q.length < 2) {
      setMateriais([]);
      return;
    }

    const [data, err] = await handleApi(
      api.post("Pesquisa/QuickSearch", {
        q,
        limit: 30,
        refvendedor: "",
      })
    );

    if (err) {
      setMateriais([]);
      return;
    }

    const lista = Array.isArray(data)
      ? data.filter((x) => String(x?.kind || "").toLowerCase() === "material")
      : [];

    setMateriais(lista);
  };

  const normalizarQuantidade = (valor) => {
    const txt = String(valor || "")
      .replace(",", ".")
      .trim();
    const n = Number(txt);
    return Number.isFinite(n) && n > 0 ? n : 1;
  };

  const pararCamera = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) videoRef.current.srcObject = null;
    } catch {}
  };

  const pararHtml5Qr = async () => {
    try {
      if (html5QrRef.current) {
        const qr = html5QrRef.current;

        try {
          if (qr.isScanning) {
            await qr.stop();
          }
        } catch {}

        try {
          await qr.clear();
        } catch {}

        html5QrRef.current = null;
      }
    } catch {}
  };

  const iniciarHtml5Qr = async () => {
    try {
      await pararHtml5Qr();

      const qr = new Html5Qrcode("html5-reader", {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.ITF,
          Html5QrcodeSupportedFormats.QR_CODE,
        ],
      });

      html5QrRef.current = qr;

      await qr.start(
        { facingMode: "environment" },
        {
          fps: 8,
          //qrbox: { width: 340, height: 180 },
        },
        async (decodedText, decodedResult) => {
          if (!decodedText) return;

          await pararHtml5Qr();

          const cod = String(decodedText || "").trim();
          const tipo = decodedResult?.result?.format?.formatName || "html5-qrcode";

          setCodigo(cod);
          setTipoCodigo(tipo);

          setResultado(null);
          setMaterialSelecionado(null);
          setMaterialInput("");
          setMateriais([]);
          setQuantidadeAssoc("1");

          showAlert(`Código detetado: ${cod}`, "success");

          await pesquisarCodigo(cod, tipo);
        },
        () => {}
      );
    } catch (err) {
      console.error("ERRO HTML5-QRCODE:", err);
      showAlert(`Erro ao iniciar leitor compatível: ${err?.message || err}`, "error");
    }
  };

  const lerComHtml5Qrcode = async (video) => {
    const canvas = document.createElement("canvas");
    const escala = 2;

    canvas.width = video.videoWidth * escala;
    canvas.height = video.videoHeight * escala;

    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/png", 1);
    });

    if (!blob) {
      throw new Error("Não foi possível capturar imagem da câmara.");
    }

    const file = new File([blob], "barcode.png", { type: "image/png" });

    await pararHtml5Qr?.();

    const qr = new Html5Qrcode("html5-reader", {
      formatsToSupport: [
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.CODE_39,
        Html5QrcodeSupportedFormats.ITF,
        Html5QrcodeSupportedFormats.QR_CODE,
      ],
    });

    html5QrRef.current = qr;

    const decodedText = await qr.scanFile(file, false);

    try {
      await qr.clear();
    } catch {}

    html5QrRef.current = null;

    return {
      codigo: decodedText || "",
      tipo: "html5-qrcode",
    };
  };

  const limpar = () => {
    setCodigo("");
    setTipoCodigo("");
    setResultado(null);
    setLoading(false);
    setCameraLoading(false);
    setMateriais([]);
    setMaterialSelecionado(null);
    setMaterialInput("");
    setQuantidadeAssoc("1");
    setAssociarLoading(false);
    setModoOperacao("");
    setQuantidadeMov("1");
    setDocumento("");
    setLocalizacao("");
  };

  const fechar = async () => {
    await pararHtml5Qr();
    pararCamera();
    limpar();
    onClose?.();
  };

  const iniciarCamera = async () => {
    setCameraLoading(true);

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        showAlert("Este browser não permite acesso à câmara neste contexto.", "warning");
        setCameraLoading(false);
        return;
      }

      pararCamera();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error("ERRO ZXING:", err);

      showAlert(`Erro ao tentar ler o código: ${err?.message || err}`, "error");
    } finally {
      setCameraLoading(false);
    }
  };

  const pesquisarCodigo = async (codigoArg = null, tipoArg = null) => {
    const cod = (codigoArg ?? codigo ?? "").trim();
    const tipo = (tipoArg ?? tipoCodigo ?? "").trim();

    if (!cod) {
      showAlert("Indica ou lê um código.", "warning");
      return;
    }

    setLoading(true);
    setResultado(null);

    const [data, err, meta] = await handleApi(
      api.post("Materiais/SelMaterialIdentificadorPorCodigo", {
        codigo: cod,
        tipo_codigo: tipo,
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    setResultado(data || null);

    if (data) {
      setMaterialSelecionado(null);
      setMaterialInput("");
      setMateriais([]);
      setQuantidadeAssoc("1");
      setModoOperacao("");
      setQuantidadeMov(String(data.quantidade || 1));
      setDocumento("");
      setLocalizacao("");
    }

    if (meta?.message) {
      showAlert(meta.message, meta.severity || "success");
    }
  };

  const associarCodigoMaterial = async () => {
    const cod = (codigo || "").trim();

    if (!cod) {
      showAlert("Lê ou indica primeiro um código.", "warning");
      return;
    }

    const refmaterial = String(
      materialSelecionado?.refmaterial || materialSelecionado?.ref || ""
    ).trim();

    if (!refmaterial) {
      showAlert("Seleciona o material a associar.", "warning");
      return;
    }

    const quantidade = normalizarQuantidade(quantidadeAssoc);

    setAssociarLoading(true);

    const [data, err, meta] = await handleApi(
      api.post("Materiais/InsMaterialIdentificador", {
        codigo: cod,
        tipo_codigo: (tipoCodigo || "").trim(),
        refmaterial,
        quantidade,
        descricao: materialSelecionado?.title || materialSelecionado?.nome || "",
        origem: "LEITOR_NAVBAR",
      })
    );

    setAssociarLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    if (meta?.message) {
      showAlert(meta.message, meta.severity || "success");
    }

    await pesquisarCodigo(cod, tipoCodigo);
  };

  const corrigirAssociacao = async () => {
    if (!resultado?.id) {
      showAlert("Não foi encontrada uma associação ativa para corrigir.", "warning");
      return;
    }

    setAssociarLoading(true);

    const [data, err, meta] = await handleApi(
      api.post("Materiais/UpdMaterialIdentificador", {
        id: resultado.id,
        quantidade: resultado.quantidade || 1,
        descricao: resultado.descricao || "",
        inativo: 1,
      })
    );

    setAssociarLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    if (meta?.message) {
      showAlert("Associação anterior inativada. Escolhe agora o material correto.", "warning");
    }

    setResultado(null);
    setMaterialSelecionado(null);
    setMaterialInput("");
    setMateriais([]);
    setQuantidadeAssoc(String(resultado.quantidade || 1));
  };

  const capturarELer = async () => {
    try {
      const video = videoRef.current;

      if (!video || !video.videoWidth || !video.videoHeight) {
        showAlert("A imagem da câmara ainda não está disponível.", "warning");
        return;
      }

      let cod = "";
      let tipo = "";

      if ("BarcodeDetector" in window) {
        const detector = new window.BarcodeDetector({
          formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf", "qr_code"],
        });

        const resultados = await detector.detect(video);

        if (resultados && resultados.length > 0) {
          cod = resultados[0].rawValue || "";
          tipo = resultados[0].format || "";
        }
      } else {
        const lido = await lerComHtml5Qrcode(video);
        cod = lido.codigo;
        tipo = lido.tipo;
      }

      if (!cod) {
        showAlert("Não foi detetado nenhum código nesta imagem.", "warning");
        return;
      }

      setCodigo(cod);
      setTipoCodigo(tipo);

      setResultado(null);
      setMaterialSelecionado(null);
      setMaterialInput("");
      setMateriais([]);
      setQuantidadeAssoc("1");

      showAlert(`Código detetado: ${cod}`, "success");

      await pesquisarCodigo(cod, tipo);
    } catch (err) {
      console.error(err);
      showAlert("Erro ao tentar ler o código.", "error");
    }
  };

  useEffect(() => {
    if (!open) return undefined;

    const suporta = "BarcodeDetector" in window;

    setSuportaDetector(suporta);

    // Motor principal: html5-qrcode
    iniciarHtml5Qr();

    setTimeout(() => {
      codigoInputRef.current?.focus();
    }, 300);

    return () => {
      pararHtml5Qr();
      pararCamera();
    };
  }, [open]);

  return (
    <Dialog open={open} onClose={fechar} maxWidth="sm" fullWidth>
      <DialogTitle>Leitor de código</DialogTitle>

      <DialogContent dividers>
        {(loading || cameraLoading) && (
          <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
            <CircularProgress size={28} />
          </MDBox>
        )}

        <MDBox display="grid" gap={1.5}>
          <MDTypography variant="button" color={suportaDetector ? "success" : "text"}>
            {suportaDetector
              ? "Leitura nativa disponível."
              : "Leitura por compatibilidade disponível."}
          </MDTypography>

          <div
            id="html5-reader"
            style={{
              width: "100%",
              minHeight: 260,
              borderRadius: 8,
              overflow: "hidden",
            }}
          />

          <MDTypography variant="button" color="text">
            Aponta a câmara ao código. A leitura inicia automaticamente.
          </MDTypography>

          {/* {suportaDetector ? (
            <MDButton
              color="info"
              onClick={capturarELer}
              disabled={loading || cameraLoading}
              fullWidth
            >
              Capturar e ler
            </MDButton>
          ) : null} */}

          <MDBox>
            <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Código lido</div>
            <TextField
              inputRef={codigoInputRef}
              autoFocus
              size="small"
              fullWidth
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\s+/g, ""))}
              onBlur={() => {
                if (open && !codigo) {
                  setTimeout(() => codigoInputRef.current?.focus(), 100);
                }
              }}
              placeholder="Ex.: 1234567890123"
            />

            {codigo ? (
              <div
                style={{
                  marginTop: 6,
                  padding: "8px 10px",
                  borderRadius: 8,
                  background: "#f8f9fa",
                  fontSize: 20,
                  fontWeight: 700,
                  letterSpacing: 1,
                  color: "#1a2b4c",
                  textAlign: "center",
                }}
              >
                {formatarCodigoVisual(codigo)}
              </div>
            ) : null}
          </MDBox>

          <MDBox>
            <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Tipo de código</div>
            {tipoCodigo ? (
              <div
                style={{
                  marginTop: 6,
                  padding: "8px 10px",
                  borderRadius: 8,
                  background: "#f8f9fa",
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#1a2b4c",
                  textAlign: "center",
                }}
              >
                {tipoCodigo}
              </div>
            ) : (
              <TextField
                size="small"
                fullWidth
                value=""
                disabled
                placeholder="Será preenchido automaticamente"
              />
            )}
          </MDBox>

          {resultado ? (
            <MDBox p={1.5} borderRadius="8px" style={{ background: "#f8f9fa" }}>
              <MDTypography variant="button" fontWeight="bold">
                Material encontrado
              </MDTypography>

              <MDTypography variant="caption" display="block">
                Ref.: {resultado.refmaterial}
              </MDTypography>

              <MDTypography variant="caption" display="block">
                Nome: {resultado.nomematerial || ""}
              </MDTypography>

              <MDTypography variant="caption" display="block">
                Quantidade sugerida: {resultado.quantidade || 1}
              </MDTypography>

              <MDBox mt={1.5}>
                <MDButton
                  color="warning"
                  variant="outlined"
                  onClick={corrigirAssociacao}
                  disabled={associarLoading}
                >
                  Corrigir associação
                </MDButton>
              </MDBox>

              <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
                <MDButton
                  color={modoOperacao === "inventario" ? "info" : "secondary"}
                  variant={modoOperacao === "inventario" ? "gradient" : "outlined"}
                  onClick={() => setModoOperacao("inventario")}
                >
                  Inventário
                </MDButton>

                <MDButton
                  color={modoOperacao === "conferencia" ? "info" : "secondary"}
                  variant={modoOperacao === "conferencia" ? "gradient" : "outlined"}
                  onClick={() => setModoOperacao("conferencia")}
                >
                  Conferência
                </MDButton>
              </MDBox>

              {modoOperacao ? (
                <MDBox mt={2} display="grid" gap={1.25}>
                  {modoOperacao === "conferencia" && (
                    <MDBox>
                      <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>
                        Documento
                      </div>
                      <TextField
                        size="small"
                        fullWidth
                        value={documento}
                        onChange={(e) => setDocumento(e.target.value)}
                        placeholder="Ex.: guia, fatura, documento interno"
                      />
                    </MDBox>
                  )}

                  <MDBox>
                    <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>
                      Quantidade
                    </div>
                    <TextField
                      size="small"
                      fullWidth
                      value={quantidadeMov}
                      onChange={(e) => setQuantidadeMov(e.target.value)}
                      placeholder="Quantidade"
                    />
                  </MDBox>

                  <MDBox>
                    <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>
                      Localização
                    </div>
                    <TextField
                      size="small"
                      fullWidth
                      value={localizacao}
                      onChange={(e) => setLocalizacao(e.target.value)}
                      placeholder="Ex.: Armazém / corredor / estante / prateleira"
                    />
                  </MDBox>

                  <MDButton color="success" disabled>
                    {modoOperacao === "inventario"
                      ? "Registar inventário (em breve)"
                      : "Registar conferência (em breve)"}
                  </MDButton>
                </MDBox>
              ) : null}
            </MDBox>
          ) : codigo ? (
            <MDBox p={1.5} borderRadius="8px" style={{ background: "#fff7ed" }}>
              <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                Código ainda não associado
              </MDTypography>

              <MDBox display="grid" gap={1.25}>
                <MDBox>
                  <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Material</div>
                  <Autocomplete
                    size="small"
                    filterOptions={(options) => options}
                    options={materiais}
                    value={materialSelecionado}
                    inputValue={materialInput}
                    onInputChange={(_, value) => {
                      setMaterialInput(value);
                      carregarMateriais(value);
                    }}
                    onChange={(_, value) => setMaterialSelecionado(value)}
                    getOptionLabel={(option) =>
                      option?.label ||
                      option?.title ||
                      option?.nome ||
                      `${option?.ref || option?.refmaterial || ""}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      String(option?.ref || option?.refmaterial || "") ===
                      String(value?.ref || value?.refmaterial || "")
                    }
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Pesquisar material..." />
                    )}
                  />
                </MDBox>

                <MDBox>
                  <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>
                    Quantidade representada por este código
                  </div>
                  <TextField
                    size="small"
                    fullWidth
                    value={quantidadeAssoc}
                    onChange={(e) => setQuantidadeAssoc(e.target.value)}
                    placeholder="Ex.: 1, 6, 24"
                  />
                </MDBox>

                <MDButton
                  color="success"
                  onClick={associarCodigoMaterial}
                  disabled={associarLoading}
                >
                  {associarLoading ? "A associar..." : "Associar código ao material"}
                </MDButton>
              </MDBox>
            </MDBox>
          ) : null}
        </MDBox>
      </DialogContent>

      <DialogActions>
        <MDButton onClick={fechar} color="secondary">
          Fechar
        </MDButton>
        <MDButton
          onClick={() => {
            codigoInputRef.current?.focus();
            pesquisarCodigo();
          }}
          color="info"
          disabled={loading}
        >
          Pesquisar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
